import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Palette, ArrowLeft, Save, RotateCcw, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FONT_OPTIONS = [
  { value: 'Syne', label: 'Syne (Modern)' },
  { value: 'Playfair Display', label: 'Playfair Display (Elegant)' },
  { value: 'Montserrat', label: 'Montserrat (Clean)' },
  { value: 'Bebas Neue', label: 'Bebas Neue (Bold)' },
  { value: 'Roboto', label: 'Roboto (Classic)' }
];

const BODY_FONT_OPTIONS = [
  { value: 'Manrope', label: 'Manrope (Readable)' },
  { value: 'Inter', label: 'Inter (Modern)' },
  { value: 'Open Sans', label: 'Open Sans (Clean)' },
  { value: 'Lato', label: 'Lato (Friendly)' },
  { value: 'Poppins', label: 'Poppins (Popular)' }
];

export const AdminTheme = () => {
  const [theme, setTheme] = useState({
    primary_color: '#F59E0B',
    secondary_color: '#15803D',
    accent_color: '#EA580C',
    background_color: '#1C1917',
    foreground_color: '#FAFAF9',
    heading_font: 'Syne',
    body_font: 'Manrope',
    border_radius: '1rem',
    button_style: 'rounded-full'
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadTheme();
  }, []);

  const loadTheme = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/theme-settings`);
      setTheme(response.data);
    } catch (error) {
      toast.error('Failed to load theme');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await adminFetch('/admin/theme-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(theme)
      });
      
      if (response.ok) {
        toast.success('Theme saved! Refresh your store to see changes.');
      }
    } catch (error) {
      toast.error('Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset to default theme?')) return;
    
    const defaults = {
      primary_color: '#F59E0B',
      secondary_color: '#15803D',
      accent_color: '#EA580C',
      background_color: '#1C1917',
      foreground_color: '#FAFAF9',
      heading_font: 'Syne',
      body_font: 'Manrope',
      border_radius: '1rem',
      button_style: 'rounded-full'
    };
    
    setTheme(defaults);
    toast.success('Reset to defaults');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-texture py-8 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-4xl font-bold font-display text-foreground">
                Theme Customizer
              </h1>
              <p className="text-muted-foreground mt-1">
                Customize colors, fonts, and style
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-6 py-3 border border-border hover:border-primary/50 text-muted-foreground hover:text-foreground rounded-full font-bold transition-all"
            >
              <RotateCcw className="w-5 h-5" />
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold shadow-glow transition-all hover:scale-105 disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save Theme'}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Controls */}
          <div className="space-y-6">
            {/* Colors */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border/50 rounded-2xl p-6"
            >
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
                <Palette className="w-6 h-6 text-primary" />
                Colors
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Primary Color (Buttons, Links)
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="color"
                      value={theme.primary_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="w-20 h-12 rounded-lg cursor-pointer border border-border"
                    />
                    <input
                      type="text"
                      value={theme.primary_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="flex-1 px-4 py-3 bg-background border border-border rounded-lg text-foreground font-mono"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Secondary Color (Accents)
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="color"
                      value={theme.secondary_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, secondary_color: e.target.value }))}
                      className="w-20 h-12 rounded-lg cursor-pointer border border-border"
                    />
                    <input
                      type="text"
                      value={theme.secondary_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, secondary_color: e.target.value }))}
                      className="flex-1 px-4 py-3 bg-background border border-border rounded-lg text-foreground font-mono"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Accent Color (Highlights)
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="color"
                      value={theme.accent_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, accent_color: e.target.value }))}
                      className="w-20 h-12 rounded-lg cursor-pointer border border-border"
                    />
                    <input
                      type="text"
                      value={theme.accent_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, accent_color: e.target.value }))}
                      className="flex-1 px-4 py-3 bg-background border border-border rounded-lg text-foreground font-mono"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Background Color
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="color"
                      value={theme.background_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, background_color: e.target.value }))}
                      className="w-20 h-12 rounded-lg cursor-pointer border border-border"
                    />
                    <input
                      type="text"
                      value={theme.background_color}
                      onChange={(e) => setTheme(prev => ({ ...prev, background_color: e.target.value }))}
                      className="flex-1 px-4 py-3 bg-background border border-border rounded-lg text-foreground font-mono"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Typography */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card border border-border/50 rounded-2xl p-6"
            >
              <h2 className="text-2xl font-bold text-foreground mb-6">Typography</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Heading Font
                  </label>
                  <select
                    value={theme.heading_font}
                    onChange={(e) => setTheme(prev => ({ ...prev, heading_font: e.target.value }))}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground"
                  >
                    {FONT_OPTIONS.map(font => (
                      <option key={font.value} value={font.value}>{font.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Body Font
                  </label>
                  <select
                    value={theme.body_font}
                    onChange={(e) => setTheme(prev => ({ ...prev, body_font: e.target.value }))}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground"
                  >
                    {BODY_FONT_OPTIONS.map(font => (
                      <option key={font.value} value={font.value}>{font.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </motion.div>

            {/* Style */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card border border-border/50 rounded-2xl p-6"
            >
              <h2 className="text-2xl font-bold text-foreground mb-6">Style</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-foreground mb-2">
                    Button Style
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setTheme(prev => ({ ...prev, button_style: 'rounded-full' }))}
                      className={`px-6 py-3 rounded-full font-bold transition-all ${
                        theme.button_style === 'rounded-full'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background border border-border text-muted-foreground'
                      }`}
                    >
                      Rounded
                    </button>
                    <button
                      onClick={() => setTheme(prev => ({ ...prev, button_style: 'rounded-lg' }))}
                      className={`px-6 py-3 rounded-lg font-bold transition-all ${
                        theme.button_style === 'rounded-lg'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background border border-border text-muted-foreground'
                      }`}
                    >
                      Square
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: Preview */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-card border border-border/50 rounded-2xl p-6 sticky top-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-3">
                <Eye className="w-6 h-6 text-primary" />
                Preview
              </h2>
            </div>

            <div 
              className="space-y-6 p-6 rounded-xl"
              style={{ 
                backgroundColor: theme.background_color,
                color: theme.foreground_color,
                fontFamily: theme.body_font
              }}
            >
              <h1 
                className="text-4xl font-bold"
                style={{ 
                  fontFamily: theme.heading_font,
                  color: theme.foreground_color 
                }}
              >
                Escola de Samba Casa Samba
              </h1>
              
              <p style={{ color: theme.foreground_color, opacity: 0.7 }}>
                This is how your body text will look. Lorem ipsum dolor sit amet, consectetur adipiscing elit.
              </p>

              <div className="flex gap-3 flex-wrap">
                <button
                  className={`px-6 py-3 font-bold transition-all ${theme.button_style}`}
                  style={{ backgroundColor: theme.primary_color, color: '#000' }}
                >
                  Primary Button
                </button>
                <button
                  className={`px-6 py-3 font-bold transition-all ${theme.button_style}`}
                  style={{ backgroundColor: theme.secondary_color, color: '#fff' }}
                >
                  Secondary Button
                </button>
                <button
                  className={`px-6 py-3 font-bold transition-all ${theme.button_style}`}
                  style={{ backgroundColor: theme.accent_color, color: '#fff' }}
                >
                  Accent Button
                </button>
              </div>

              <div 
                className="p-4 rounded-lg"
                style={{ backgroundColor: theme.primary_color, opacity: 0.1 }}
              >
                <p style={{ color: theme.foreground_color }}>
                  Sample card with primary color background
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Help */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-8 bg-primary/10 border border-primary/20 rounded-2xl p-6"
        >
          <h3 className="font-bold text-foreground mb-2">💡 How it works</h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Adjust colors using the color pickers or hex codes</li>
            <li>• Choose fonts that match your brand</li>
            <li>• Preview changes in real-time on the right</li>
            <li>• Click "Save Theme" when ready</li>
            <li>• Refresh your store to see the new theme!</li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
};
